/* =======================================================================
   BYHIMSELF — UX Upgrade layer  (bh-ux.js v1.0)
   No dependencies, no framework. Every module is defensive: if the markup it
   needs is absent, it does nothing. Nothing here overrides Dawn behaviour —
   the add-to-cart, cart drawer and faceted filtering stay Dawn's.
   ========================================================================== */
(function () {
  'use strict';

  var isFR = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
  var t = function (en, fr) { return isFR ? fr : en; };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{amount}}') !== -1) return format.replace('{{amount}}', value);
    return '$' + value;
  }

  /* ------------------------------------------------------------------
     1. Lazy-loading + decoding hygiene
     Any image the theme or an app injected without loading/decoding hints
     gets them — except the first viewport (LCP must stay eager).
     ------------------------------------------------------------------ */
  function lazyImages() {
    var imgs = document.querySelectorAll('img:not([data-bh-lazy])');
    Array.prototype.forEach.call(imgs, function (img) {
      img.setAttribute('data-bh-lazy', '');
      if (!img.hasAttribute('decoding')) img.decoding = 'async';
      if (img.hasAttribute('loading')) return;
      var box = img.getBoundingClientRect();
      img.loading = box.top < window.innerHeight ? 'eager' : 'lazy';
    });
  }

  /* ------------------------------------------------------------------
     2. Search — popular queries + natural language routing
     Dawn's predictive search stays in charge of results. We add: intent
     chips, and a rewrite that turns a sentence ("something warm for my
     bedroom") into a query the storefront search actually understands.
     ------------------------------------------------------------------ */
  var INTENTS = [
    // [matchers, storefront query, human label EN, human label FR]
    [['bedroom', 'chambre', 'sleep', 'dormir', 'nightstand'], 'bedroom', 'Bedroom', 'Chambre'],
    [['living room', 'salon', 'lounge', 'sofa', 'couch', 'coffee table'], 'living room', 'Living room', 'Salon'],
    [['kitchen', 'cuisine', 'dining', 'salle a manger', 'table'], 'kitchen dining', 'Kitchen & dining', 'Cuisine'],
    [['bathroom', 'salle de bain', 'spa'], 'bathroom', 'Bathroom', 'Salle de bain'],
    [['office', 'bureau', 'desk', 'workspace'], 'office desk', 'Office', 'Bureau'],
    [['entryway', 'entree', 'hallway', 'corridor'], 'entryway', 'Entryway', 'Entrée'],
    [['warm', 'cosy', 'cozy', 'chaleureux', 'hygge', 'ambiance', 'mood', 'glow'], 'lamp candle light', 'Warm lighting', 'Lumière chaude'],
    [['dried', 'sech', 'pampas', 'branch', 'stem', 'vase', 'fleur', 'flower'], 'vase', 'Vases', 'Vases'],
    [['gift', 'cadeau', 'present', 'housewarming', 'pendaison'], 'gift', 'Gift ideas', 'Idées cadeau'],
    [['small space', 'petit espace', 'condo', 'apartment', 'studio'], 'small shelf tray', 'Small spaces', 'Petits espaces'],
    [['wall', 'mur', 'art', 'print', 'poster', 'affiche', 'cadre'], 'wall art print', 'Wall art', 'Déco murale'],
    [['minimal', 'minimaliste', 'scandinav', 'nordic', 'nordique', 'japandi'], 'minimalist', 'Minimalist', 'Minimaliste'],
    [['boho', 'boheme', 'rattan', 'woven'], 'boho', 'Boho', 'Boho'],
    [['industrial', 'industriel', 'metal', 'iron', 'concrete', 'beton'], 'industrial metal', 'Industrial', 'Industriel']
  ];

  var STOPWORDS = ('i im am looking for something a an the some to my me want need with that which of in on and or est je cherche quelque chose ' +
    'pour mon ma mes un une des du de la le les qui avec dans et ou quelquechose').split(' ');

  function rewriteQuery(raw) {
    var q = (raw || '').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, ''); // strip accents: "entrée" matches "entree"
    if (q.trim().length < 3) return null;
    var hits = [];
    INTENTS.forEach(function (row) {
      for (var i = 0; i < row[0].length; i++) {
        if (q.indexOf(row[0][i]) !== -1) { hits.push(row); return; }
      }
    });
    if (!hits.length) return null;
    // Only rewrite conversational input — a short keyword query is already fine.
    var words = q.split(/\s+/).filter(Boolean);
    var meaningful = words.filter(function (w) { return STOPWORDS.indexOf(w) === -1; });
    if (words.length < 3 && meaningful.length <= 1) return null;
    var terms = [];
    hits.slice(0, 2).forEach(function (row) {
      row[1].split(' ').forEach(function (term) { if (terms.indexOf(term) === -1) terms.push(term); });
    });
    return {
      query: terms.join(' '),
      labels: hits.slice(0, 2).map(function (row) { return t(row[2], row[3]); })
    };
  }

  function cloneTemplate(id) {
    var tpl = document.getElementById(id);
    if (!tpl || !('content' in tpl)) return null;
    return tpl.content.cloneNode(true);
  }

  function searchEnhance() {
    var forms = document.querySelectorAll('form[action$="/search"]');
    if (!forms.length) return;

    Array.prototype.forEach.call(forms, function (form) {
      var input = form.querySelector('input[name="q"]');
      if (!input || input.hasAttribute('data-bh-search')) return;
      input.setAttribute('data-bh-search', '');

      // Popular searches, shown on focus while the field is still empty
      var host = form.closest('predictive-search') || form.parentElement;
      if (host && !host.querySelector('[data-bh-suggest]')) {
        var fragment = cloneTemplate('bh-tpl-search-suggest');
        if (fragment) host.appendChild(fragment);
      }
      var panel = host && host.querySelector('[data-bh-suggest]');

      if (panel) {
        panel.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-bh-term]');
          if (!btn) return;
          e.preventDefault();
          input.value = btn.getAttribute('data-bh-term');
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.focus();
        });
        var togglePanel = function () {
          panel.hidden = input.value.trim().length > 0;
        };
        input.addEventListener('focus', togglePanel);
        input.addEventListener('input', togglePanel);
        togglePanel();
      }

      // Natural-language rewrite on submit
      form.addEventListener('submit', function () {
        var rewrite = rewriteQuery(input.value);
        if (!rewrite) return;
        try { sessionStorage.setItem('bh:nlq', input.value); } catch (err) { /* private mode */ }
        input.value = rewrite.query;
      });
    });

    // On the results page, tell the shopper what we actually searched for.
    // Silently swapping someone's words would be the wrong kind of clever.
    var searchHeader = document.querySelector('.template-search__header, #main-search .collection-hero__title');
    if (!searchHeader || document.querySelector('[data-bh-search-note]')) return;

    var original = null;
    try { original = sessionStorage.getItem('bh:nlq'); } catch (err) { original = null; }
    if (!original) return;

    var current = new URLSearchParams(window.location.search).get('q') || '';
    if (original.toLowerCase() === current.toLowerCase()) return;

    var note = document.createElement('p');
    note.className = 'bh-search-hint';
    note.setAttribute('data-bh-search-note', '');
    note.style.margin = '1.6rem 0 0';
    note.innerHTML = t(
      'Showing results for <strong>' + current + '</strong> — from what you asked: “' + original + '”.',
      'Résultats pour <strong>' + current + '</strong> — d’après votre demande : « ' + original + ' ».'
    );
    searchHeader.appendChild(note);
    try { sessionStorage.removeItem('bh:nlq'); } catch (err) { /* noop */ }
  }

  /* ------------------------------------------------------------------
     3b. Cart — progress steps + trust block, in the drawer and on the page
     ------------------------------------------------------------------ */
  function cartTrust() {
    var targets = [];
    // Fix (2026-09-21): the trust block (3-step progress + 4 bullet points +
    // payment-icon row) was being injected into the CART-DRAWER footer, right
    // before the Checkout button (.cart__ctas). On real device viewport
    // heights this pushed the Checkout button out of the drawer's scrollable
    // area, making it unreachable — customers could add to cart and open the
    // drawer, but could never tap "Check out". Confirmed against Shopify
    // abandoned-checkout data (repeated same-product abandons within minutes)
    // and zero completed purchases despite healthy ad/pixel funnel. The trust
    // content still renders fine on the full /cart page footer below; it is
    // simply no longer duplicated inside the space-constrained drawer.
    var pageFooter = document.querySelector('#main-cart-footer .cart__footer, #main-cart-footer .js-contents');
    if (pageFooter) targets.push(pageFooter);

    targets.forEach(function (host) {
      if (host.querySelector('[data-bh-cart-trust]')) return;
      var fragment = cloneTemplate('bh-tpl-cart-trust');
      if (!fragment) return;
      var ctas = host.querySelector('.cart__ctas');
      if (ctas && ctas.parentNode === host) host.insertBefore(fragment, ctas);
      else host.appendChild(fragment);
    });
  }

  /* ------------------------------------------------------------------
     3. Faceted filtering — sticky bar, active chips, mobile FAB
     Dawn already does the AJAX. We make the state readable at a glance.
     ------------------------------------------------------------------ */
  function facets() {
    var facetsWrapper = document.getElementById('main-collection-filters') ||
      document.querySelector('.facets-container');
    if (!facetsWrapper) return;

    facetsWrapper.classList.add('bh-facets-sticky');

    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    facetsWrapper.parentNode.insertBefore(sentinel, facetsWrapper);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        facetsWrapper.classList.toggle('bh-is-pinned', !entries[0].isIntersecting);
      }, { threshold: 0 }).observe(sentinel);
    }

    // Colour swatches on the Color facet
    var SWATCHES = {
      black: '#1e1b18', white: '#ffffff', beige: '#e3d9c8', cream: '#f2ece1', brown: '#7b5a43',
      green: '#4a6b50', gold: '#c8a44b', pink: '#e2b6b1', clear: 'transparent', silver: '#c8c8c8',
      grey: '#9a9a9a', gray: '#9a9a9a', blue: '#4a6280', terracotta: '#b5705b', natural: '#d4cbc0',
      noir: '#1e1b18', blanc: '#ffffff', vert: '#4a6b50', brun: '#7b5a43', or: '#c8a44b'
    };
    Array.prototype.forEach.call(facetsWrapper.querySelectorAll('.facet-checkbox, .mobile-facets__label'), function (label) {
      if (label.querySelector('.bh-swatch')) return;
      var text = (label.textContent || '').trim().toLowerCase().split('(')[0].trim();
      var hex = SWATCHES[text];
      if (!hex) return;
      var dot = document.createElement('span');
      dot.className = 'bh-swatch';
      dot.style.background = hex;
      if (hex === 'transparent') dot.style.background = 'linear-gradient(135deg,#fff 45%,#ddd 55%)';
      var target = label.querySelector('input') ? label.querySelector('input').nextElementSibling : null;
      label.insertBefore(dot, target && target.nextSibling ? target.nextSibling : label.firstChild);
    });

    // Mobile filter FAB — sits in the thumb zone, opens Dawn's own drawer
    var mobileTrigger = facetsWrapper.querySelector('.mobile-facets__open, .disclosure-has-popup summary');
    if (mobileTrigger && !document.querySelector('.bh-filter-fab')) {
      var fab = document.createElement('button');
      fab.type = 'button';
      fab.className = 'bh-filter-fab';
      fab.innerHTML = '<span>' + t('Filter &amp; sort', 'Filtrer et trier') + '</span>';
      fab.addEventListener('click', function () {
        mobileTrigger.click();
        mobileTrigger.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      document.body.appendChild(fab);
      var toggleFab = function () {
        var grid = document.getElementById('ProductGridContainer');
        if (!grid) return;
        var box = grid.getBoundingClientRect();
        fab.classList.toggle('bh-is-visible', box.top < 0 && box.bottom > window.innerHeight * 0.4);
      };
      window.addEventListener('scroll', toggleFab, { passive: true });
      toggleFab();
    }

    // Fade the grid while Dawn refetches, so the change is legible
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.facets') && !e.target.closest('.mobile-facets')) return;
      var container = document.getElementById('ProductGridContainer');
      if (!container) return;
      container.classList.add('bh-facets-loading');
      setTimeout(function () { container.classList.remove('bh-facets-loading'); }, 900);
    });
  }

  /* ------------------------------------------------------------------
     4. Product page — reposition our sections around the buy button
     Sections render at the bottom of the template (safe, no core edits),
     then we move the short ones into the info column where they convert.
     ------------------------------------------------------------------ */
  function productLayout() {
    var info = document.querySelector('.product__info-container, .product__info-wrapper');
    if (!info) return;

    var anchors = {
      'before-title': info.querySelector('.product__title'),
      'after-price': info.querySelector('.price') ? info.querySelector('.price').closest('.product__info-container > *, div') : null,
      'after-buy-buttons': info.querySelector('.product-form__buttons') || info.querySelector('product-form')
    };

    Array.prototype.forEach.call(document.querySelectorAll('[data-bh-move]'), function (node) {
      var where = node.getAttribute('data-bh-move');
      var anchor = anchors[where];
      if (!anchor || !anchor.parentNode) return;
      if (where === 'before-title') anchor.parentNode.insertBefore(node, anchor);
      else anchor.parentNode.insertBefore(node, anchor.nextSibling);
      node.hidden = false;
    });

    if (document.querySelector('.bh-sticky-atc, [data-bh-sticky-atc]')) {
      document.body.classList.add('bh-has-sticky-atc');
    }
  }

  /* ------------------------------------------------------------------
     5. Delivery estimate — turns "7–15 business days" into real dates
     ------------------------------------------------------------------ */
  function deliveryEstimate() {
    var nodes = document.querySelectorAll('[data-bh-eta]');
    if (!nodes.length) return;
    var MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

    function addBusinessDays(date, days) {
      var d = new Date(date.getTime());
      var added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        var day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return d;
    }
    function fmt(d) {
      var months = isFR ? MONTHS_FR : MONTHS_EN;
      return isFR ? d.getDate() + ' ' + months[d.getMonth()] : months[d.getMonth()] + ' ' + d.getDate();
    }

    Array.prototype.forEach.call(nodes, function (node) {
      var min = parseInt(node.getAttribute('data-bh-eta-min'), 10) || 7;
      var max = parseInt(node.getAttribute('data-bh-eta-max'), 10) || 15;
      var handling = parseInt(node.getAttribute('data-bh-eta-handling'), 10) || 2;
      var now = new Date();
      var from = addBusinessDays(now, handling + min);
      var to = addBusinessDays(now, handling + max);
      node.textContent = fmt(from) + ' – ' + fmt(to);
      node.setAttribute('datetime', from.toISOString().slice(0, 10));
    });
  }

  /* ------------------------------------------------------------------
     6. Complete the room — complementary products, then related as fallback
     ------------------------------------------------------------------ */
  function recommendations() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bh-reco]'), function (host) {
      var base = host.getAttribute('data-bh-reco');
      if (!base) return;
      var sectionId = host.getAttribute('data-bh-reco-section');
      var limit = host.getAttribute('data-bh-reco-limit') || 4;
      // Fix (2026-09-21): Shopify's /recommendations/products endpoint
      // requires product_id or it 422s on every call. This attribute was
      // never set, so every page load fired two guaranteed-failing fetches
      // (complementary, then the related fallback) — dead weight on every
      // product page, worst on mobile ad-click traffic already fighting for
      // bandwidth.
      var productId = host.getAttribute('data-bh-reco-product');
      if (!productId) return;

      function load(intent, onEmpty) {
        var url = base + '?product_id=' + productId + '&section_id=' + sectionId + '&limit=' + limit + '&intent=' + intent;
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var grid = doc.querySelector('[data-bh-reco-grid]');
            if (!grid || !grid.children.length) { if (onEmpty) onEmpty(); return; }
            var target = host.querySelector('[data-bh-reco-grid]');
            if (!target) return;
            target.innerHTML = grid.innerHTML;
            host.hidden = false;
            lazyImages();
          })
          .catch(function () { if (onEmpty) onEmpty(); });
      }

      load('complementary', function () { load('related'); });
    });
  }

  /* ------------------------------------------------------------------
     7. Add-to-cart micro-interaction — the product flies to the cart icon
     ------------------------------------------------------------------ */
  function addToCartFeedback() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-form__submit, .quick-add__submit, [name="add"]');
      if (!btn || btn.disabled) return;

      btn.classList.add('bh-atc-firing');
      setTimeout(function () { btn.classList.remove('bh-atc-firing'); }, 460);
      if (reduceMotion) return;

      var scope = btn.closest('.card-wrapper, .product, .product__info-container') || document;
      var img = scope.querySelector('.card__media img, .product__media img, img');
      var cartIcon = document.querySelector('#cart-icon-bubble, .header__icon--cart');
      if (!img || !cartIcon) return;

      var from = img.getBoundingClientRect();
      var to = cartIcon.getBoundingClientRect();
      if (!from.width || !to.width) return;

      var ghost = document.createElement('img');
      ghost.src = img.currentSrc || img.src;
      ghost.alt = '';
      ghost.className = 'bh-atc-flash';
      ghost.style.left = from.left + 'px';
      ghost.style.top = from.top + 'px';
      ghost.style.width = from.width + 'px';
      ghost.style.height = from.height + 'px';
      document.body.appendChild(ghost);

      requestAnimationFrame(function () {
        var dx = to.left + to.width / 2 - (from.left + from.width / 2);
        var dy = to.top + to.height / 2 - (from.top + from.height / 2);
        ghost.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(0.12)';
        ghost.style.opacity = '0.15';
      });
      setTimeout(function () { ghost.remove(); }, 700);
    }, true);
  }

  /* ------------------------------------------------------------------
     8. Shop the room — hotspots ↔ product list
     ------------------------------------------------------------------ */
  function shopTheRoom() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bh-room]'), function (room) {
      var spots = room.querySelectorAll('.bh-hotspot');
      var items = room.querySelectorAll('.bh-room__product');

      function activate(key) {
        Array.prototype.forEach.call(spots, function (s) {
          s.setAttribute('aria-expanded', String(s.getAttribute('data-bh-spot') === key));
        });
        Array.prototype.forEach.call(items, function (i) {
          var on = i.getAttribute('data-bh-spot') === key;
          i.classList.toggle('bh-is-active', on);
          if (on && window.innerWidth < 990) {
            i.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
          }
        });
      }

      Array.prototype.forEach.call(spots, function (spot) {
        spot.addEventListener('click', function () { activate(spot.getAttribute('data-bh-spot')); });
        spot.addEventListener('mouseenter', function () { activate(spot.getAttribute('data-bh-spot')); });
      });
      Array.prototype.forEach.call(items, function (item) {
        item.addEventListener('mouseenter', function () { activate(item.getAttribute('data-bh-spot')); });
        item.addEventListener('focus', function () { activate(item.getAttribute('data-bh-spot')); });
      });
    });
  }

  /* ------------------------------------------------------------------
     9. Style quiz — answers map to a collection, no back-end needed
     ------------------------------------------------------------------ */
  function styleQuiz() {
    var quiz = document.querySelector('[data-bh-quiz]');
    if (!quiz) return;

    var steps = Array.prototype.slice.call(quiz.querySelectorAll('.bh-quiz__step'));
    var result = quiz.querySelector('[data-bh-quiz-result]');
    var bar = quiz.querySelector('.bh-quiz__bar');
    var scores = {};
    var index = 0;

    function show(i) {
      steps.forEach(function (s, n) { s.hidden = n !== i; });
      if (result) result.hidden = true;
      if (bar) bar.style.width = (i / steps.length) * 100 + '%';
      var q = steps[i] && steps[i].querySelector('.bh-quiz__q');
      if (q) q.setAttribute('tabindex', '-1'), q.focus({ preventScroll: true });
    }

    function finish() {
      var winner = null;
      Object.keys(scores).forEach(function (k) {
        if (!winner || scores[k] > scores[winner]) winner = k;
      });
      if (!winner) return;
      if (bar) bar.style.width = '100%';
      steps.forEach(function (s) { s.hidden = true; });
      if (!result) return;
      Array.prototype.forEach.call(result.querySelectorAll('[data-bh-quiz-outcome]'), function (out) {
        out.hidden = out.getAttribute('data-bh-quiz-outcome') !== winner;
      });
      result.hidden = false;
      result.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      try { sessionStorage.setItem('bh:style', winner); } catch (err) { /* noop */ }
    }

    quiz.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-bh-quiz-value]');
      if (opt) {
        var value = opt.getAttribute('data-bh-quiz-value');
        scores[value] = (scores[value] || 0) + 1;
        index++;
        if (index >= steps.length) finish();
        else show(index);
        return;
      }
      if (e.target.closest('[data-bh-quiz-restart]')) {
        scores = {}; index = 0; show(0);
      }
    });

    show(0);
  }

  /* ------------------------------------------------------------------
     10. 360° viewer — drag through a frame sequence when one is supplied
     ------------------------------------------------------------------ */
  function spinViewer() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bh-spin]'), function (viewer) {
      var frames;
      try { frames = JSON.parse(viewer.getAttribute('data-bh-spin')); } catch (err) { return; }
      if (!frames || frames.length < 2) return;
      var img = viewer.querySelector('img');
      if (!img) return;
      var current = 0, startX = null, startFrame = 0;

      function render(i) {
        current = ((i % frames.length) + frames.length) % frames.length;
        img.src = frames[current];
      }
      function down(x) { startX = x; startFrame = current; viewer.classList.add('bh-is-dragging'); }
      function move(x) {
        if (startX === null) return;
        var delta = Math.round((x - startX) / 12);
        render(startFrame + delta);
      }
      function up() { startX = null; viewer.classList.remove('bh-is-dragging'); }

      viewer.addEventListener('mousedown', function (e) { e.preventDefault(); down(e.clientX); });
      window.addEventListener('mousemove', function (e) { move(e.clientX); });
      window.addEventListener('mouseup', up);
      viewer.addEventListener('touchstart', function (e) { down(e.touches[0].clientX); }, { passive: true });
      viewer.addEventListener('touchmove', function (e) { move(e.touches[0].clientX); }, { passive: true });
      viewer.addEventListener('touchend', up);
      viewer.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') render(current + 1);
        if (e.key === 'ArrowLeft') render(current - 1);
      });
      render(0);
    });
  }

  /* ------------------------------------------------------------------
     11. Cart — keep the trust block and progress in sync after Dawn updates
     ------------------------------------------------------------------ */
  function cartSync() {
    var refresh = function () { lazyImages(); cartTrust(); };
    document.addEventListener('cart:refresh', refresh);

    // Dawn replaces .drawer__inner wholesale after every cart change, which
    // wipes anything we injected — watch for it instead of guessing a delay.
    var drawer = document.querySelector('cart-drawer');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(function () { cartTrust(); }).observe(drawer, { childList: true, subtree: true });
    }
    if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(refresh, 60); });
    }
  }

  ready(function () {
    lazyImages();
    searchEnhance();
    facets();
    cartTrust();
    productLayout();
    deliveryEstimate();
    recommendations();
    addToCartFeedback();
    shopTheRoom();
    styleQuiz();
    spinViewer();
    cartSync();
  });

  // Sections re-render in the theme editor — re-run everything that is idempotent
  document.addEventListener('shopify:section:load', function () {
    lazyImages(); searchEnhance(); facets(); cartTrust(); productLayout();
    deliveryEstimate(); recommendations(); shopTheRoom(); styleQuiz(); spinViewer();
  });

  window.BHUX = { money: money, rewriteQuery: rewriteQuery, lazyImages: lazyImages };
})();
